import { useMemo, useState } from 'react';

const EMPTY_LEADER = {
  id: '',
  category: 'leadership', // 'leadership' | 'internationalGovernors' | 'advisors'
  name: '',
  role: '',
  titleDetails: '',
  image: '',
  bio: ''
};

const EMPTY_PROJECT = {
  id: '',
  title: '',
  tag: '',
  image: '',
  shortDescription: '',
  description: ''
};

const EMPTY_GALLERY = {
  id: '',
  title: '',
  image: '',
  shortDescription: ''
};

const EMPTY_BANNER = {
  id: '',
  image: '',
  title: '',
  subtitle: '',
  ctaText: '',
  ctaLink: ''
};

const EMPTY_STAT = {
  value: '',
  label: ''
};

const EMPTY_CONTACT = {
  email: 'info@ungdo.org',
  phone: '+1 (000) 000-0000',
  location: 'United Nations Global Development Organization Head Office,\nDelaware, USA',
  facebook: 'https://web.facebook.com/unglobaldevorg',
  hoursWeekdays: 'Monday to Friday: 9:00AM–5:00PM',
  hoursWeekends: 'Saturday & Sunday: 11:00AM–3:00PM'
};

function AdminPanel({ siteData, setSiteData, onLogout }) {
  const [activeTab, setActiveTab] = useState('leadership');
  
  const [settingsForm, setSettingsForm] = useState({
    siteName: siteData.siteName || '',
    tagline: siteData.tagline || '',
    logo: siteData.logo || ''
  });
  
  const [aboutForm, setAboutForm] = useState(siteData.about || {
    heading: '',
    summary: '',
    mission: ''
  });

  const [leaderForm, setLeaderForm] = useState(EMPTY_LEADER);
  const [editingLeaderId, setEditingLeaderId] = useState(null);

  const [heroForm, setHeroForm] = useState(siteData.hero || {
    title: '',
    subtitle: '',
    ctaText: '',
    ctaLink: ''
  });

  const [bannerForm, setBannerForm] = useState(EMPTY_BANNER);
  const [editingBannerId, setEditingBannerId] = useState(null);

  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT);
  const [editingProjectId, setEditingProjectId] = useState(null);

  const [galleryForm, setGalleryForm] = useState(EMPTY_GALLERY);
  const [editingGalleryId, setEditingGalleryId] = useState(null);

  const [statForm, setStatForm] = useState(EMPTY_STAT);
  const [editingStatIndex, setEditingStatIndex] = useState(null);

  const [focusAreaInput, setFocusAreaInput] = useState('');
  const [contactForm, setContactForm] = useState(siteData.contact || EMPTY_CONTACT);

  const leadershipList = useMemo(() => siteData.leadership || [], [siteData.leadership]);
  const intlGovList = useMemo(() => siteData.internationalGovernors || [], [siteData.internationalGovernors]);
  const advisorsList = useMemo(() => siteData.advisors || [], [siteData.advisors]);

  const galleryList = useMemo(() => siteData.gallery || [], [siteData.gallery]);
  const projectList = useMemo(() => siteData.projects || [], [siteData.projects]);
  const bannerList = useMemo(() => siteData.bannerSlides || [], [siteData.bannerSlides]);
  const statsList = useMemo(() => siteData.stats || [], [siteData.stats]);
  const focusAreasList = useMemo(() => siteData.focusAreas || [], [siteData.focusAreas]);

  // Image File Upload Handler (Converts file to Base64 data URL)
  const handleImageFileUpload = (e, setUrlCallback) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setUrlCallback(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // BRANDING FUNCTIONS
  const updateSettings = () => {
    setSiteData((current) => ({
      ...current,
      siteName: settingsForm.siteName,
      tagline: settingsForm.tagline,
      logo: settingsForm.logo
    }));
    alert('Branding updated successfully!');
  };

  // LEADERSHIP / ABOUT US MANAGMENT FUNCTIONS
  const handleLeaderSubmit = (e) => {
    e.preventDefault();
    if (!leaderForm.name.trim() || !leaderForm.role.trim()) {
      alert('Please fill in at least the Name and Role.');
      return;
    }

    const bioArray = typeof leaderForm.bio === 'string'
      ? leaderForm.bio.split('\n\n').filter(p => p.trim() !== '')
      : leaderForm.bio;

    const targetKey = leaderForm.category; // 'leadership' | 'internationalGovernors' | 'advisors'

    setSiteData((current) => {
      const currentList = [...(current[targetKey] || [])];
      if (editingLeaderId) {
        const index = currentList.findIndex((item) => item.id === editingLeaderId);
        if (index >= 0) {
          currentList[index] = {
            ...leaderForm,
            id: editingLeaderId,
            bio: bioArray
          };
        }
      } else {
        currentList.push({
          ...leaderForm,
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
          bio: bioArray
        });
      }

      return {
        ...current,
        [targetKey]: currentList
      };
    });

    setLeaderForm(EMPTY_LEADER);
    setEditingLeaderId(null);
    alert(editingLeaderId ? 'Profile updated successfully!' : 'Profile added successfully!');
  };

  const editLeader = (member, categoryKey) => {
    const bioText = Array.isArray(member.bio) ? member.bio.join('\n\n') : member.bio || '';
    setLeaderForm({
      ...member,
      category: categoryKey,
      bio: bioText
    });
    setEditingLeaderId(member.id);
  };

  const deleteLeader = (id, categoryKey) => {
    if (window.confirm('Are you sure you want to remove this profile?')) {
      setSiteData((current) => ({
        ...current,
        [categoryKey]: (current[categoryKey] || []).filter((item) => item.id !== id)
      }));
      alert('Profile removed successfully!');
    }
  };

  // GALLERY MANAGEMENT FUNCTIONS
  const handleGallerySubmit = (e) => {
    e.preventDefault();
    if (!galleryForm.title.trim() || !galleryForm.image.trim()) {
      alert('Please provide a Title and Image.');
      return;
    }

    setSiteData((current) => {
      const gallery = [...(current.gallery || [])];
      if (editingGalleryId) {
        const index = gallery.findIndex((item) => item.id === editingGalleryId);
        if (index >= 0) gallery[index] = { ...galleryForm, id: editingGalleryId };
      } else {
        gallery.push({
          ...galleryForm,
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString()
        });
      }
      return { ...current, gallery };
    });

    setGalleryForm(EMPTY_GALLERY);
    setEditingGalleryId(null);
    alert(editingGalleryId ? 'Gallery image updated!' : 'Gallery image added!');
  };

  const deleteGalleryItem = (galleryId) => {
    if (window.confirm('Delete this gallery image?')) {
      setSiteData((current) => ({
        ...current,
        gallery: (current.gallery || []).filter((item) => item.id !== galleryId)
      }));
      alert('Gallery image deleted!');
    }
  };

  // CONTACT MANAGEMENT FUNCTIONS
  const updateContact = (e) => {
    e.preventDefault();
    setSiteData((current) => ({
      ...current,
      contact: contactForm
    }));
    alert('Contact details updated successfully!');
  };

  // HERO SECTION & BANNER SLIDES FUNCTIONS
  const updateHero = () => {
    setSiteData((current) => ({ ...current, hero: heroForm }));
    alert('Hero section updated successfully!');
  };

  const handleBannerSubmit = (event) => {
    event.preventDefault();
    if (!bannerForm.title.trim() || !bannerForm.image.trim()) return;

    setSiteData((current) => {
      const banners = [...(current.bannerSlides || [])];
      if (editingBannerId) {
        const index = banners.findIndex((item) => item.id === editingBannerId);
        if (index >= 0) banners[index] = { ...bannerForm, id: editingBannerId };
      } else {
        banners.push({ ...bannerForm, id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() });
      }
      return { ...current, bannerSlides: banners };
    });
    setBannerForm(EMPTY_BANNER);
    setEditingBannerId(null);
    alert(editingBannerId ? 'Slide updated!' : 'Slide added!');
  };

  const deleteBanner = (bannerId) => {
    if (window.confirm('Delete this banner slide?')) {
      setSiteData((current) => ({
        ...current,
        bannerSlides: (current.bannerSlides || []).filter((item) => item.id !== bannerId)
      }));
      alert('Slide deleted!');
    }
  };

  // PROJECT FUNCTIONS
  const handleProjectSubmit = (event) => {
    event.preventDefault();
    if (!projectForm.title.trim()) return;

    setSiteData((current) => {
      const projects = [...(current.projects || [])];
      if (editingProjectId) {
        const index = projects.findIndex((item) => item.id === editingProjectId);
        if (index >= 0) projects[index] = { ...projectForm, id: editingProjectId };
      } else {
        projects.push({ ...projectForm, id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() });
      }
      return { ...current, projects };
    });
    setProjectForm(EMPTY_PROJECT);
    setEditingProjectId(null);
    alert(editingProjectId ? 'Project updated!' : 'Project added!');
  };

  const deleteProject = (projectId) => {
    if (window.confirm('Delete this project?')) {
      setSiteData((current) => ({
        ...current,
        projects: (current.projects || []).filter((item) => item.id !== projectId)
      }));
      alert('Project deleted!');
    }
  };

  // ABOUT SECTION
  const updateAbout = () => {
    setSiteData((current) => ({ ...current, about: aboutForm }));
    alert('About overview updated successfully!');
  };

  // FOCUS AREAS
  const addFocusArea = () => {
    if (!focusAreaInput.trim()) return;
    setSiteData((current) => ({
      ...current,
      focusAreas: [...(current.focusAreas || []), focusAreaInput.trim()]
    }));
    setFocusAreaInput('');
    alert('Focus area added!');
  };

  const deleteFocusArea = (index) => {
    if (window.confirm('Delete this focus area?')) {
      setSiteData((current) => ({
        ...current,
        focusAreas: (current.focusAreas || []).filter((_, i) => i !== index)
      }));
      alert('Focus area deleted!');
    }
  };

  // STATS
  const handleStatSubmit = (event) => {
    event.preventDefault();
    if (!statForm.value.trim() || !statForm.label.trim()) return;

    setSiteData((current) => {
      const stats = [...(current.stats || [])];
      if (editingStatIndex !== null) {
        stats[editingStatIndex] = statForm;
      } else {
        stats.push(statForm);
      }
      return { ...current, stats };
    });
    setStatForm(EMPTY_STAT);
    setEditingStatIndex(null);
    alert(editingStatIndex !== null ? 'Stat updated!' : 'Stat added!');
  };

  const deleteStat = (index) => {
    if (window.confirm('Delete this stat?')) {
      setSiteData((current) => ({
        ...current,
        stats: (current.stats || []).filter((_, i) => i !== index)
      }));
      alert('Stat deleted!');
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header admin-header-row">
        <div>
          <p className="eyebrow">Admin Portal</p>
          <h2>Website Management System</h2>
        </div>
        <button
          type="button"
          className="secondary-button"
          onClick={() => {
            localStorage.removeItem('ungdo-admin-auth');
            if (typeof onLogout === 'function') onLogout();
          }}
        >
          Logout
        </button>
      </div>

      {/* NAVIGATION TABS */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', flexWrap: 'wrap' }}>
        {[
          { id: 'leadership', label: '👥 Leadership Profiles (About Us)' },
          { id: 'gallery', label: '🖼️ Gallery Images' },
          { id: 'contact', label: '📞 Contact Details' },
          { id: 'hero', label: '✨ Hero & Banner Slides' },
          { id: 'projects', label: '📊 Projects' },
          { id: 'aboutText', label: '📝 About Overview' },
          { id: 'branding', label: '🎨 Branding' }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'primary-button' : 'secondary-button'}
            style={{ fontSize: '0.92rem', padding: '10px 16px' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB 1: LEADERSHIP PROFILES (ABOUT US PAGE) */}
      {activeTab === 'leadership' && (
        <section className="admin-section">
          <h3>Manage Leadership & Board Profiles (About Us Page)</h3>
          <p style={{ color: '#405a73', marginBottom: '20px' }}>
            Add, edit, remove profile entries or upload new profile pictures for Executive Leadership, International Governors, and Advisors.
          </p>

          <form onSubmit={handleLeaderSubmit} className="form-grid" style={{ background: '#ffffff', padding: '24px', borderRadius: '16px', border: '1px solid #d0e2f5', marginBottom: '32px' }}>
            <h4 style={{ color: '#0c2f4d', margin: '0 0 16px' }} className="full-width">
              {editingLeaderId ? '✏️ Edit Leadership Profile' : '➕ Add New Leadership Profile'}
            </h4>

            <label>
              Profile Section / Category *
              <select
                value={leaderForm.category}
                onChange={(e) => setLeaderForm({ ...leaderForm, category: e.target.value })}
                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d0e2f5', fontSize: '0.95rem' }}
              >
                <option value="leadership">Executive Leadership & Board of Directors</option>
                <option value="internationalGovernors">Board of Governors - International Governors</option>
                <option value="advisors">Board of Governors - Advisors</option>
              </select>
            </label>

            <label>
              Role / Position Badge *
              <input
                type="text"
                placeholder="e.g. Chairman, Vice Chairman, International Governor"
                value={leaderForm.role}
                onChange={(e) => setLeaderForm({ ...leaderForm, role: e.target.value })}
                required
              />
            </label>

            <label className="full-width">
              Full Name *
              <input
                type="text"
                placeholder="e.g. His Eminence Prof. Dr. Sir Madhu Krishan"
                value={leaderForm.name}
                onChange={(e) => setLeaderForm({ ...leaderForm, name: e.target.value })}
                required
              />
            </label>

            <label className="full-width">
              Degrees / Credentials / Title Details (Optional)
              <input
                type="text"
                placeholder="e.g. GG, AJP GIA (London), M.B.A (London)"
                value={leaderForm.titleDetails || ''}
                onChange={(e) => setLeaderForm({ ...leaderForm, titleDetails: e.target.value })}
              />
            </label>

            <label className="full-width">
              Profile Photo URL or Path
              <input
                type="text"
                placeholder="Paste Image URL / Path (e.g. /images/chairman.png)"
                value={leaderForm.image || ''}
                onChange={(e) => setLeaderForm({ ...leaderForm, image: e.target.value })}
              />
            </label>

            <label className="full-width">
              📁 Or Upload Profile Image File
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageFileUpload(e, (imgUrl) => setLeaderForm({ ...leaderForm, image: imgUrl }))}
                style={{ background: '#f0f7ff', padding: '10px' }}
              />
            </label>

            {leaderForm.image && (
              <div className="full-width" style={{ marginTop: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#405a73', display: 'block', marginBottom: '6px' }}>Photo Preview:</span>
                <img src={leaderForm.image} alt="Preview" style={{ width: '120px', height: '140px', objectFit: 'cover', borderRadius: '12px', border: '2px solid #009edb' }} />
              </div>
            )}

            <label className="full-width">
              Biography Paragraphs (Separate paragraphs with a blank line)
              <textarea
                rows="6"
                placeholder="Enter full biography description..."
                value={typeof leaderForm.bio === 'string' ? leaderForm.bio : (leaderForm.bio || []).join('\n\n')}
                onChange={(e) => setLeaderForm({ ...leaderForm, bio: e.target.value })}
              />
            </label>

            <div className="button-row full-width" style={{ marginTop: '12px' }}>
              <button type="submit" className="primary-button">
                {editingLeaderId ? 'Save Profile Changes' : 'Add Profile'}
              </button>
              {editingLeaderId && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setLeaderForm(EMPTY_LEADER);
                    setEditingLeaderId(null);
                  }}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>

          {/* LIST OF EXECUTIVE LEADERSHIP */}
          <div style={{ marginBottom: '36px' }}>
            <h4 style={{ color: '#0c2f4d', borderBottom: '2px solid #e6f0ff', paddingBottom: '8px' }}>
              Executive Leadership & Board of Directors ({leadershipList.length})
            </h4>
            <div className="list-stack" style={{ marginTop: '16px' }}>
              {leadershipList.map((member) => (
                <div className="admin-item" key={member.id || member.name} style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: '16px', alignItems: 'center' }}>
                  <img src={member.image} alt={member.name} style={{ width: '70px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                  <div>
                    <span style={{ background: '#0c2f4d', color: '#f9c84a', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700' }}>{member.role}</span>
                    <h4 style={{ margin: '4px 0 2px', color: '#0c2f4d' }}>{member.name}</h4>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#405a73' }}>{member.titleDetails || 'No additional credentials listed'}</p>
                  </div>
                  <div className="button-row">
                    <button type="button" className="secondary-button" onClick={() => editLeader(member, 'leadership')}>Edit</button>
                    <button type="button" className="danger-button" onClick={() => deleteLeader(member.id, 'leadership')}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* LIST OF INTERNATIONAL GOVERNORS */}
          <div style={{ marginBottom: '36px' }}>
            <h4 style={{ color: '#0c2f4d', borderBottom: '2px solid #e6f0ff', paddingBottom: '8px' }}>
              Board of Governors - International Governors ({intlGovList.length})
            </h4>
            <div className="list-stack" style={{ marginTop: '16px' }}>
              {intlGovList.map((member) => (
                <div className="admin-item" key={member.id || member.name} style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: '16px', alignItems: 'center' }}>
                  <img src={member.image} alt={member.name} style={{ width: '70px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                  <div>
                    <span style={{ background: '#009edb', color: '#ffffff', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700' }}>{member.role}</span>
                    <h4 style={{ margin: '4px 0 2px', color: '#0c2f4d' }}>{member.name}</h4>
                  </div>
                  <div className="button-row">
                    <button type="button" className="secondary-button" onClick={() => editLeader(member, 'internationalGovernors')}>Edit</button>
                    <button type="button" className="danger-button" onClick={() => deleteLeader(member.id, 'internationalGovernors')}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* LIST OF ADVISORS */}
          <div>
            <h4 style={{ color: '#0c2f4d', borderBottom: '2px solid #e6f0ff', paddingBottom: '8px' }}>
              Board of Governors - Advisors ({advisorsList.length})
            </h4>
            <div className="list-stack" style={{ marginTop: '16px' }}>
              {advisorsList.map((member) => (
                <div className="admin-item" key={member.id || member.name} style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: '16px', alignItems: 'center' }}>
                  <img src={member.image} alt={member.name} style={{ width: '70px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                  <div>
                    <span style={{ background: '#0b5091', color: '#ffffff', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700' }}>{member.role}</span>
                    <h4 style={{ margin: '4px 0 2px', color: '#0c2f4d' }}>{member.name}</h4>
                  </div>
                  <div className="button-row">
                    <button type="button" className="secondary-button" onClick={() => editLeader(member, 'advisors')}>Edit</button>
                    <button type="button" className="danger-button" onClick={() => deleteLeader(member.id, 'advisors')}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TAB 2: GALLERY MANAGEMENT */}
      {activeTab === 'gallery' && (
        <section className="admin-section">
          <h3>Gallery Images Management</h3>
          <p style={{ color: '#405a73', marginBottom: '20px' }}>
            Add, edit, or delete gallery photos displayed across the homepage and gallery sections.
          </p>

          <form onSubmit={handleGallerySubmit} className="form-grid" style={{ background: '#ffffff', padding: '24px', borderRadius: '16px', border: '1px solid #d0e2f5', marginBottom: '32px' }}>
            <h4 style={{ color: '#0c2f4d', margin: '0 0 16px' }} className="full-width">
              {editingGalleryId ? '✏️ Edit Gallery Image' : '➕ Add Gallery Image'}
            </h4>

            <label className="full-width">
              Image Title *
              <input
                type="text"
                placeholder="e.g. UNGDO Leadership Symposium"
                value={galleryForm.title}
                onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                required
              />
            </label>

            <label className="full-width">
              Short Description / Caption
              <input
                type="text"
                placeholder="e.g. Global partners gathered for peace advocacy."
                value={galleryForm.shortDescription || ''}
                onChange={(e) => setGalleryForm({ ...galleryForm, shortDescription: e.target.value })}
              />
            </label>

            <label className="full-width">
              Image Web URL or Path
              <input
                type="text"
                placeholder="Paste Image URL / Path (e.g. https://... or /images/gallary.jpg)"
                value={galleryForm.image}
                onChange={(e) => setGalleryForm({ ...galleryForm, image: e.target.value })}
              />
            </label>

            <label className="full-width">
              📁 Or Upload Image File
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageFileUpload(e, (imgUrl) => setGalleryForm({ ...galleryForm, image: imgUrl }))}
                style={{ background: '#f0f7ff', padding: '10px' }}
              />
            </label>

            {galleryForm.image && (
              <div className="full-width" style={{ marginTop: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: '#405a73', display: 'block', marginBottom: '6px' }}>Image Preview:</span>
                <img src={galleryForm.image} alt="Preview" style={{ width: '220px', height: '140px', objectFit: 'cover', borderRadius: '12px', border: '2px solid #009edb' }} />
              </div>
            )}

            <div className="button-row full-width" style={{ marginTop: '12px' }}>
              <button type="submit" className="primary-button">
                {editingGalleryId ? 'Update Gallery Image' : 'Add Gallery Image'}
              </button>
              {editingGalleryId && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setGalleryForm(EMPTY_GALLERY);
                    setEditingGalleryId(null);
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="list-stack gallery-stack">
            {galleryList.map((item) => (
              <div className="admin-item admin-gallery-item" key={item.id}>
                <img src={item.image} alt={item.title} style={{ width: '160px', height: '110px', objectFit: 'cover', borderRadius: '12px' }} />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 6px', color: '#0c2f4d' }}>{item.title}</h4>
                  <p style={{ margin: '0 0 12px', color: '#405a73', fontSize: '0.9rem' }}>{item.shortDescription || 'No description provided.'}</p>
                  <div className="button-row">
                    <button type="button" className="secondary-button" onClick={() => { setGalleryForm(item); setEditingGalleryId(item.id); }}>Edit</button>
                    <button type="button" className="danger-button" onClick={() => deleteGalleryItem(item.id)}>Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TAB 3: CONTACT DETAILS */}
      {activeTab === 'contact' && (
        <section className="admin-section">
          <h3>Contact Details Management</h3>
          <p style={{ color: '#405a73', marginBottom: '20px' }}>
            Edit email address, headquarters location, phone, social links, and office hours displayed across the site header, footer, and contact page.
          </p>

          <form onSubmit={updateContact} className="form-grid" style={{ background: '#ffffff', padding: '24px', borderRadius: '16px', border: '1px solid #d0e2f5' }}>
            <label className="full-width">
              Official Contact Email Address *
              <input
                type="email"
                value={contactForm.email || 'info@ungdo.org'}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
              />
            </label>

            <label className="full-width">
              Headquarters Location / Address
              <textarea
                rows="3"
                value={contactForm.location || ''}
                onChange={(e) => setContactForm({ ...contactForm, location: e.target.value })}
              />
            </label>

            <label>
              Official Phone Number
              <input
                type="text"
                value={contactForm.phone || ''}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
              />
            </label>

            <label>
              Facebook Page URL
              <input
                type="url"
                value={contactForm.facebook || ''}
                onChange={(e) => setContactForm({ ...contactForm, facebook: e.target.value })}
              />
            </label>

            <label>
              Weekday Office Hours
              <input
                type="text"
                value={contactForm.hoursWeekdays || 'Monday to Friday: 9:00AM–5:00PM'}
                onChange={(e) => setContactForm({ ...contactForm, hoursWeekdays: e.target.value })}
              />
            </label>

            <label>
              Weekend Office Hours
              <input
                type="text"
                value={contactForm.hoursWeekends || 'Saturday & Sunday: 11:00AM–3:00PM'}
                onChange={(e) => setContactForm({ ...contactForm, hoursWeekends: e.target.value })}
              />
            </label>

            <div className="full-width" style={{ marginTop: '16px' }}>
              <button type="submit" className="primary-button" style={{ padding: '12px 28px', fontSize: '1rem' }}>
                💾 Save Contact Information
              </button>
            </div>
          </form>
        </section>
      )}

      {/* TAB 4: HERO & BANNERS */}
      {activeTab === 'hero' && (
        <>
          <section className="admin-section">
            <h3>Hero Section</h3>
            <div className="form-grid">
              <label className="full-width">
                Heading
                <input value={heroForm.title || ''} onChange={(event) => setHeroForm({ ...heroForm, title: event.target.value })} />
              </label>
              <label className="full-width">
                Subtitle
                <textarea value={heroForm.subtitle || ''} onChange={(event) => setHeroForm({ ...heroForm, subtitle: event.target.value })} rows={3} />
              </label>
              <label>
                CTA Text
                <input value={heroForm.ctaText || ''} onChange={(event) => setHeroForm({ ...heroForm, ctaText: event.target.value })} />
              </label>
              <label>
                CTA Link
                <input value={heroForm.ctaLink || ''} onChange={(event) => setHeroForm({ ...heroForm, ctaLink: event.target.value })} placeholder="e.g. /about" />
              </label>
            </div>
            <button className="primary-button" onClick={updateHero}>Save hero section</button>
          </section>

          <section className="admin-section">
            <h3>Homepage Banner Slideshow</h3>
            <form onSubmit={handleBannerSubmit} className="form-grid">
              <label className="full-width">
                Banner Title
                <input value={bannerForm.title} onChange={(event) => setBannerForm({ ...bannerForm, title: event.target.value })} />
              </label>
              <label className="full-width">
                Banner Subtitle
                <textarea value={bannerForm.subtitle} onChange={(event) => setBannerForm({ ...bannerForm, subtitle: event.target.value })} rows={2} />
              </label>
              <label className="full-width">
                Image Web URL or Path
                <input value={bannerForm.image} onChange={(event) => setBannerForm({ ...bannerForm, image: event.target.value })} />
              </label>
              <label className="full-width">
                📁 Or Upload Banner Image File
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageFileUpload(e, (imgUrl) => setBannerForm({ ...bannerForm, image: imgUrl }))}
                  style={{ background: '#f0f7ff', padding: '10px' }}
                />
              </label>
              <label>
                CTA Text
                <input value={bannerForm.ctaText} onChange={(event) => setBannerForm({ ...bannerForm, ctaText: event.target.value })} />
              </label>
              <label>
                CTA Link
                <input value={bannerForm.ctaLink} onChange={(event) => setBannerForm({ ...bannerForm, ctaLink: event.target.value })} placeholder="e.g. /about" />
              </label>
              <div className="button-row full-width">
                <button type="submit" className="primary-button">{editingBannerId ? 'Update slide' : 'Add slide'}</button>
                {editingBannerId ? (
                  <button type="button" className="secondary-button" onClick={() => { setBannerForm(EMPTY_BANNER); setEditingBannerId(null); }}>Cancel</button>
                ) : null}
              </div>
            </form>

            <div className="list-stack">
              {bannerList.map((banner) => (
                <div className="admin-item" key={banner.id}>
                  <img src={banner.image} alt={banner.title} style={{ width: '120px', height: '70px', objectFit: 'cover', borderRadius: '8px' }} />
                  <div>
                    <strong>{banner.title}</strong>
                    <span>{banner.subtitle}</span>
                  </div>
                  <div className="button-row">
                    <button type="button" className="secondary-button" onClick={() => { setBannerForm(banner); setEditingBannerId(banner.id); }}>Edit</button>
                    <button type="button" className="danger-button" onClick={() => deleteBanner(banner.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* TAB 5: PROJECTS */}
      {activeTab === 'projects' && (
        <section className="admin-section">
          <h3>Featured Projects Management</h3>
          <form onSubmit={handleProjectSubmit} className="form-grid">
            <label>
              Title
              <input value={projectForm.title} onChange={(event) => setProjectForm({ ...projectForm, title: event.target.value })} />
            </label>
            <label>
              Tag
              <input value={projectForm.tag} onChange={(event) => setProjectForm({ ...projectForm, tag: event.target.value })} />
            </label>
            <label className="full-width">
              Image URL or Path
              <input value={projectForm.image} onChange={(event) => setProjectForm({ ...projectForm, image: event.target.value })} />
            </label>
            <label className="full-width">
              📁 Or Upload Project Image File
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageFileUpload(e, (imgUrl) => setProjectForm({ ...projectForm, image: imgUrl }))}
                style={{ background: '#f0f7ff', padding: '10px' }}
              />
            </label>
            <label className="full-width">
              Short description
              <textarea value={projectForm.shortDescription} onChange={(event) => setProjectForm({ ...projectForm, shortDescription: event.target.value })} rows={2} />
            </label>
            <label className="full-width">
              Full description
              <textarea value={projectForm.description} onChange={(event) => setProjectForm({ ...projectForm, description: event.target.value })} rows={4} />
            </label>
            <div className="button-row full-width">
              <button type="submit" className="primary-button">{editingProjectId ? 'Update project' : 'Add project'}</button>
              {editingProjectId ? (
                <button type="button" className="secondary-button" onClick={() => { setProjectForm(EMPTY_PROJECT); setEditingProjectId(null); }}>Cancel</button>
              ) : null}
            </div>
          </form>

          <div className="list-stack">
            {projectList.map((project) => (
              <div className="admin-item" key={project.id}>
                <div>
                  <strong>{project.title}</strong>
                  <span>{project.tag}</span>
                </div>
                <div className="button-row">
                  <button type="button" className="secondary-button" onClick={() => { setProjectForm(project); setEditingProjectId(project.id); }}>Edit</button>
                  <button type="button" className="danger-button" onClick={() => deleteProject(project.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TAB 6: ABOUT TEXT */}
      {activeTab === 'aboutText' && (
        <>
          <section className="admin-section">
            <h3>About Page Text Overview</h3>
            <div className="form-grid">
              <label className="full-width">
                Heading
                <input value={aboutForm.heading || ''} onChange={(event) => setAboutForm({ ...aboutForm, heading: event.target.value })} />
              </label>
              <label className="full-width">
                Summary
                <textarea value={aboutForm.summary || ''} onChange={(event) => setAboutForm({ ...aboutForm, summary: event.target.value })} rows={4} />
              </label>
              <label className="full-width">
                Mission
                <textarea value={aboutForm.mission || ''} onChange={(event) => setAboutForm({ ...aboutForm, mission: event.target.value })} rows={4} />
              </label>
            </div>
            <button className="primary-button" onClick={updateAbout}>Save about text</button>
          </section>

          <section className="admin-section">
            <h3>Focus Areas</h3>
            <div className="form-grid">
              <label className="full-width">
                Add Focus Area
                <input 
                  value={focusAreaInput} 
                  onChange={(event) => setFocusAreaInput(event.target.value)}
                  onKeyPress={(event) => { if (event.key === 'Enter') { event.preventDefault(); addFocusArea(); } }}
                />
              </label>
            </div>
            <button className="primary-button" onClick={addFocusArea}>Add focus area</button>

            <div className="list-stack" style={{ marginTop: '16px' }}>
              {focusAreasList.map((area, index) => (
                <div className="admin-item" key={index}>
                  <div><strong>{area}</strong></div>
                  <button type="button" className="danger-button" onClick={() => deleteFocusArea(index)}>Delete</button>
                </div>
              ))}
            </div>
          </section>

          <section className="admin-section">
            <h3>Statistics</h3>
            <form onSubmit={handleStatSubmit} className="form-grid">
              <label>
                Value (e.g., "15+")
                <input value={statForm.value} onChange={(event) => setStatForm({ ...statForm, value: event.target.value })} />
              </label>
              <label>
                Label (e.g., "Years of engagement")
                <input value={statForm.label} onChange={(event) => setStatForm({ ...statForm, label: event.target.value })} />
              </label>
              <div className="button-row full-width">
                <button type="submit" className="primary-button">{editingStatIndex !== null ? 'Update stat' : 'Add stat'}</button>
                {editingStatIndex !== null ? (
                  <button type="button" className="secondary-button" onClick={() => { setStatForm(EMPTY_STAT); setEditingStatIndex(null); }}>Cancel</button>
                ) : null}
              </div>
            </form>

            <div className="list-stack">
              {statsList.map((stat, index) => (
                <div className="admin-item" key={index}>
                  <div>
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                  <div className="button-row">
                    <button type="button" className="secondary-button" onClick={() => { setStatForm(stat); setEditingStatIndex(index); }}>Edit</button>
                    <button type="button" className="danger-button" onClick={() => deleteStat(index)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* TAB 7: BRANDING */}
      {activeTab === 'branding' && (
        <section className="admin-section">
          <h3>Branding & Settings</h3>
          <div className="form-grid">
            <label>
              Site name
              <input value={settingsForm.siteName || ''} onChange={(event) => setSettingsForm({ ...settingsForm, siteName: event.target.value })} />
            </label>
            <label>
              Tagline
              <input value={settingsForm.tagline || ''} onChange={(event) => setSettingsForm({ ...settingsForm, tagline: event.target.value })} />
            </label>
            <label className="full-width">
              Logo URL or Path
              <input value={settingsForm.logo || ''} onChange={(event) => setSettingsForm({ ...settingsForm, logo: event.target.value })} />
            </label>
            <label className="full-width">
              📁 Or Upload Logo File
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageFileUpload(e, (imgUrl) => setSettingsForm({ ...settingsForm, logo: imgUrl }))}
                style={{ background: '#f0f7ff', padding: '10px' }}
              />
            </label>
          </div>
          <button className="primary-button" onClick={updateSettings}>Save branding</button>
        </section>
      )}
    </div>
  );
}

export default AdminPanel;
